/* eslint-disable */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import React, { useRef } from "react";
import { useEffect, useState } from "react";
import { columnDetailInterface } from "../../../../types/type";
import ShareBtn from "../../../../shared/ui/button/share/ShareBtn";
import UxEvaluationTag from "../../home/tag/UxEvaluationTag";
import AuthorContactBtn, {
  AuthorContactBtnProps,
} from "../../home/article-list/ui/AuthorContactBtn";
import { AUTHOR_CONTACT_TYPE, DATA_SOURCE } from "../../../../types/enum";
import { ShowAuthorContactBtn } from "../../mobile/ShowAuthorContactBtn";
import {
  DetailSkeleton,
  Spinner,
  QueryErrorFallback,
} from "../../../../shared/ui";
import { useArticleDetailQuery } from "../config/useArticleDetailQuery";
import { useImageLoad } from "../../../../shared/hooks";
import { useLanguage } from "../../../../shared/hooks/useLanguageContext";
import {
  createMultilingualArticle,
  createMultilingualProduct,
  createMultilingualImageDescription,
} from "../../../../shared/utils/multilingual-helpers";
import { useTranslation } from "../../../../shared/hooks/useTranslation";

// DetailImageWithSpinner 컴포넌트를 외부로 분리하여 매번 새로 생성되지 않도록 함
const DetailImageWithSpinner = React.memo(
  ({
    imageUrl,
    descriptionKo,
    descriptionEn,
    index,
  }: {
    imageUrl: string;
    descriptionKo?: string;
    descriptionEn?: string;
    index: number;
  }) => {
    const { data: loadedImageUrl, isLoading, error } = useImageLoad(imageUrl);
    const { getLocalizedText } = useLanguage();
    const { t } = useTranslation();

    // 다국어 설명 처리
    const multilingualDescription = createMultilingualImageDescription({
      descKo: descriptionKo || "",
      descEn: descriptionEn || "",
    });
    const localizedDescription = getLocalizedText(
      multilingualDescription.description,
    );

    if (isLoading) {
      return (
        <div className="image-desc-wrapper">
          <div className="image-loading-container">
            <Spinner size="medium" className="detail-spinner" />
          </div>
        </div>
      );
    }

    if (error || !loadedImageUrl) {
      return (
        <div className="image-desc-wrapper">
          <div className="image-loading-container">
            <div className="image-error">{t("article.imageError")}</div>
          </div>
        </div>
      );
    }

    return (
      <div className="image-desc-wrapper">
        <img
          src={loadedImageUrl}
          alt={`${index}번째 설명 이미지`}
          className="screen-image visible"
        />
        {localizedDescription && (
          <div className="img-desc-box">
            <div className="img-desc-title-box">
              <img
                src="/assets/icons/FiUxInsight.svg"
                alt="UX 인사이트 타이틀 아이콘"
                className="ux-insight-icon"
              />
              <div className="insight-notes-title">
                {t("article.uxInsight")}
              </div>
            </div>
            <div className="desc">{localizedDescription}</div>
          </div>
        )}
      </div>
    );
  },
);

// ProductImageWithSpinner 컴포넌트도 외부로 분리
const ProductImageWithSpinner = React.memo(
  ({ imageUrl }: { imageUrl: string | undefined }) => {
    const { data: loadedImageUrl, isLoading, error } = useImageLoad(imageUrl);
    const { t } = useTranslation();

    if (isLoading) {
      return (
        <div className="product-icon-container">
          <Spinner size="small" className="product-spinner" />
        </div>
      );
    }

    if (error || !loadedImageUrl) {
      return (
        <div className="product-icon-container">
          <div className="product-icon-error">{t("article.noImage")}</div>
        </div>
      );
    }

    return (
      <img alt="product-icon" className="product-icon" src={loadedImageUrl} />
    );
  },
);

export default function Detail(props: columnDetailInterface) {
  const url = window.location.href;
  const imgWrapperRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    data: columnData,
    isLoading,
    error,
  } = useArticleDetailQuery(props.id);

  // 다국어 지원을 위한 훅
  const { getLocalizedText } = useLanguage();
  const { t } = useTranslation();

  useEffect(() => {
    const handleArrowVisibility = () => {
      if (imgWrapperRef.current) {
        const scrollLeft = imgWrapperRef.current.scrollLeft;
        const maxScrollLeft =
          imgWrapperRef.current.scrollWidth - imgWrapperRef.current.clientWidth;

        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft < maxScrollLeft);
      }
    };

    // columnData가 있고 이미지가 로드된 후에 화살표 상태를 확인
    if (columnData && columnData.images && columnData.images.length > 0) {
      // 이미지 로딩을 위한 약간의 지연
      const timeoutId = setTimeout(() => {
        handleArrowVisibility();
      }, 100);

      const imgWrapper = imgWrapperRef.current;
      if (imgWrapper) {
        imgWrapper.addEventListener("scroll", handleArrowVisibility);

        // ResizeObserver를 추가하여 이미지 로드 후 화살표 상태 재확인
        const resizeObserver = new ResizeObserver(() => {
          // 약간의 지연을 두어 이미지 로드 완료 후 실행
          setTimeout(handleArrowVisibility, 50);
        });

        resizeObserver.observe(imgWrapper);

        // 이미지 요소들도 관찰
        const imageElements = imgWrapper.querySelectorAll(
          ".image-desc-wrapper",
        );
        imageElements.forEach((element) => {
          resizeObserver.observe(element);
        });

        return () => {
          clearTimeout(timeoutId);
          imgWrapper.removeEventListener("scroll", handleArrowVisibility);
          resizeObserver.disconnect();
        };
      }
    }
  }, [columnData]);

  const handleScroll = (direction: number) => {
    if (imgWrapperRef.current) {
      const imgWrapper = imgWrapperRef.current;
      const imgElements = imgWrapper.querySelectorAll(".image-desc-wrapper");

      if (!imgElements.length) return;

      const wrapperWidth = imgWrapper.clientWidth; // 한 번에 보이는 영역의 너비
      const currentScroll = imgWrapper.scrollLeft;
      const totalWidth = imgWrapper.scrollWidth;
      // const imgWidth = imgElements[0].clientWidth; // 이미지 1개의 너비 (간격 포함)

      if (direction === 1) {
        // 오른쪽 화살표 클릭
        const nextScroll = Math.min(
          currentScroll + wrapperWidth,
          totalWidth - wrapperWidth,
        );
        imgWrapper.scrollTo({ left: nextScroll, behavior: "smooth" });
      } else {
        // 왼쪽 화살표 클릭
        const prevScroll = Math.max(currentScroll - wrapperWidth, 0);
        imgWrapper.scrollTo({ left: prevScroll, behavior: "smooth" });
      }
    }
  };

  const toggleExpand = () => {
    setIsExpanded((prevState) => !prevState);
  };

  useEffect(() => {
    const element = contentRef.current;
    if (!element || !columnData) return;

    const checkOverflow = () => {
      const lineHeight = parseFloat(
        getComputedStyle(element).lineHeight || "0",
      );
      const maxHeight = lineHeight * 5;
      setIsOverflow(element.scrollHeight > maxHeight);
    };

    // 약간의 지연을 두어 DOM이 완전히 렌더링된 후 체크
    const timeoutId = setTimeout(checkOverflow, 100);

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(element);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [columnData]); // columnData가 변경될 때마다 다시 체크

  const authorLinkForMobile: AuthorContactBtnProps[] = [
    props.data?.author.authorInsta && {
      btnType: AUTHOR_CONTACT_TYPE.INSTAGRAM,
      link: props.data?.author.authorInsta,
    },
    props.data?.author.authorLinkedin && {
      btnType: AUTHOR_CONTACT_TYPE.LINKEDIN,
      link: props.data?.author.authorLinkedin,
    },
    props.data?.author.authorEmail && {
      btnType: AUTHOR_CONTACT_TYPE.EMAIL,
      link: props.data?.author.authorEmail,
    },
    {
      btnType: AUTHOR_CONTACT_TYPE.ARTICLE_LINK,
      link: props.id || "",
    },
  ].filter(Boolean) as AuthorContactBtnProps[];

  if (isLoading) {
    return <DetailSkeleton />;
  }

  if (error) {
    return (
      <QueryErrorFallback
        error={error as Error}
        title={t("article.loadArticleError")}
        description={t("article.loadArticleErrorDesc")}
      />
    );
  }

  if (!columnData) {
    return (
      <div className="detail-page">
        <div className="error-message">{t("article.articleNotFound")}</div>
      </div>
    );
  }

  // 다국어 데이터 변환
  const multilingualTitle = createMultilingualArticle({
    titleKo: columnData.titleKo,
    titleEn: columnData.titleEn,
    contentKo: columnData.contentKo,
    contentEn: columnData.contentEn,
  });

  const multilingualProduct = columnData.product
    ? createMultilingualProduct({
        nameKo: columnData.product.nameKo,
        nameEn: columnData.product.nameEn,
      })
    : null;

  return (
    <div className="detail-page">
      <div className="top-line">
        <div className="top-left">
          <div className="title">
            {getLocalizedText(multilingualTitle.title)}
          </div>
          <div className="title-divide-bar"></div>
          <ProductImageWithSpinner imageUrl={columnData.product?.imageUrl} />
          <div className="product-name">
            {multilingualProduct
              ? getLocalizedText(multilingualProduct.name)
              : ""}
          </div>
          <UxEvaluationTag name={columnData.uxEvaluation!} />
        </div>
        <div className="top-right">
          <ShareBtn copyUrl={url} />
          <div className="close-btn" onClick={() => props.closeModal()}>
            <img
              src={"/assets/icons/Xbutton.svg"}
              alt="close-btn"
              className="close-btn-img"
            />
          </div>
        </div>
      </div>
      <div className="center-content">
        <div className="content-container">
          <div
            className={`content-left ${isExpanded ? "expanded" : "collapsed"}`}
            ref={contentRef}
          >
            {getLocalizedText(multilingualTitle.content)}
          </div>
          <div className="divide-line"></div>
          <div className="article-link">
            <div className="article-link-title">{t("article.studyLinks")}</div>
            {columnData.studyLinks?.map((article, idx) => {
              return (
                <div className="article-wrapper" key={idx}>
                  <img src={"/assets/icons/LinkLogo.svg"} alt="close-btn" />
                  <a key={idx} href={article} target="_blank" rel="noreferrer">
                    {article}
                  </a>
                </div>
              );
            })}
          </div>
          {isOverflow && (
            <button className="more-content" onClick={toggleExpand}>
              {isExpanded ? t("common.hide") : t("common.more")}
            </button>
          )}
        </div>
        <div className="divide-vertical-line"></div>

        <div className="ux-evaluation-tag-mobile">
          <UxEvaluationTag name={columnData.uxEvaluation!} />

          <div className="img-slider-container">
            {showLeftArrow && (
              <button className="left-arrow" onClick={() => handleScroll(-1)}>
                <img src={"/assets/icons/ArrowRight.svg"} alt="left-btn" />
              </button>
            )}
            <div className="img-wrapper" ref={imgWrapperRef}>
              {columnData.images
                ?.sort((a, b) => a.order - b.order)
                ?.map((el, idx) => (
                  <DetailImageWithSpinner
                    key={el.imageUrl! + idx}
                    imageUrl={el.imageUrl!}
                    descriptionKo={el.descKo}
                    descriptionEn={el.descEn}
                    index={idx}
                  />
                ))}
            </div>
            {showRightArrow && (
              <button className="right-arrow" onClick={() => handleScroll(1)}>
                <img src={"/assets/icons/ArrowRight.svg"} alt="left-btn" />
              </button>
            )}
          </div>
        </div>
        <div className="article-link-container-for-mobile">
          <div className="article-link">
            <div className="article-link-title">{t("article.studyLinks")}</div>
            {columnData.studyLinks?.map((article, idx) => {
              return (
                <div className="article-wrapper" key={idx}>
                  <img src={"/assets/icons/LinkLogo.svg"} alt="close-btn" />
                  <a key={idx} href={article} target="_blank" rel="noreferrer">
                    {article}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <div className="author-wrapper">
          <div className="author-title">
            <div className="author-text">{t("article.author")}</div>
            <div className="author-name">{columnData.author.authorName}</div>
          </div>
          <div className="author-contact-box">
            {columnData.author.authorEmail !== "" ? (
              <AuthorContactBtn
                btnType={AUTHOR_CONTACT_TYPE.EMAIL}
                link={columnData.author.authorEmail || ""}
              />
            ) : null}
            {columnData.author.authorInsta !== "" ? (
              <AuthorContactBtn
                btnType={AUTHOR_CONTACT_TYPE.INSTAGRAM}
                link={columnData.author.authorInsta || ""}
              />
            ) : null}
            {columnData.author.authorLinkedin !== "" ? (
              <AuthorContactBtn
                btnType={AUTHOR_CONTACT_TYPE.LINKEDIN}
                link={columnData.author.authorLinkedin || ""}
              />
            ) : null}
          </div>

          <div className="author-contact-mobile-box">
            <ShowAuthorContactBtn
              key={props.id}
              keyId={props.id || ""}
              btnType={authorLinkForMobile}
              productName={
                multilingualProduct
                  ? getLocalizedText(multilingualProduct.name)
                  : ""
              }
              dataSource={columnData.dataSource}
            />
          </div>
        </div>
        <div className="copyright-wrapper">
          {t("copyright.uiCopyright")}{" "}
          {multilingualProduct
            ? getLocalizedText(multilingualProduct.name)
            : ""}{" "}
          {columnData.dataSource !== DATA_SOURCE.CUSTOM
            ? `, ${t("copyright.viaMobbin")}`
            : null}
        </div>
      </div>
    </div>
  );
}
